/**
 * Синхронизация часов с сервером (упрощённый NTP).
 *
 * Часы на устройствах пользователей расходятся на секунды, а иногда и на
 * минуты. Поэтому нельзя сравнивать серверный `anchorServerMs` с локальным
 * `Date.now()` напрямую — сначала нужно вычислить поправку.
 */
/**
 * Один замер по схеме NTP.
 *
 *   t0 — клиент отправил, t1 — сервер ответил, t2 — клиент получил.
 *
 * Предполагаем, что задержка симметрична, тогда серверное время в момент t1
 * соответствовало локальному (t0 + t2) / 2.
 */
export function computeSample(t0, t1, t2) {
    return {
        rttMs: t2 - t0,
        offsetMs: t1 - (t0 + t2) / 2,
    };
}
/**
 * Оценка поправки по набору замеров.
 *
 * Берём медиану трети замеров с наименьшим RTT: выбросы из-за случайных
 * задержек сети портят среднее, а быстрые ответы почти всегда симметричны.
 */
export function estimateOffset(samples) {
    if (samples.length === 0)
        return 0;
    const sorted = [...samples].sort((a, b) => a.rttMs - b.rttMs);
    const keep = Math.max(1, Math.ceil(sorted.length / 3));
    const best = sorted.slice(0, keep).map((s) => s.offsetMs).sort((a, b) => a - b);
    const mid = Math.floor(best.length / 2);
    if (best.length % 2 === 1)
        return best[mid] ?? 0;
    return ((best[mid - 1] ?? 0) + (best[mid] ?? 0)) / 2;
}
/**
 * Хранит поправку к часам и выдаёт «серверное сейчас».
 *
 * Замеры накапливаются в скользящем окне: сеть меняется, а часы устройства
 * могут подвести часы по NTP прямо во время просмотра.
 */
export class ServerClock {
    samples = [];
    offsetMs = 0;
    windowSize;
    constructor(windowSize = 12) {
        this.windowSize = windowSize;
    }
    addSample(sample) {
        this.samples.push(sample);
        if (this.samples.length > this.windowSize) {
            this.samples.shift();
        }
        this.offsetMs = estimateOffset(this.samples);
    }
    /** Текущее серверное время в мс. */
    now() {
        return Date.now() + this.offsetMs;
    }
    getOffsetMs() {
        return this.offsetMs;
    }
    /** Лучший (наименьший) RTT в окне — индикатор качества связи. */
    getBestRttMs() {
        if (this.samples.length === 0)
            return null;
        return Math.min(...this.samples.map((s) => s.rttMs));
    }
    /** Замеров достаточно, чтобы доверять поправке. */
    isReady() {
        return this.samples.length >= 3;
    }
    reset() {
        this.samples = [];
        this.offsetMs = 0;
    }
}
//# sourceMappingURL=clock.js.map